import { motion } from 'framer-motion';
import styled, { css } from 'styled-components';

import BaseButton from 'components/Button/Button';
import Carousel from 'components/Carousel/Carousel';
import DoubleColorHeadline from 'components/DoubleColorHeadline/DoubleColorHeadline';
import Draggable from 'components/Draggable/Draggable';
import BaseFilter from 'components/Filter';
import Headline from 'components/Headline/Headline';
import Hero from 'components/Hero/Hero';
import InfiniteSlider from 'components/InfiniteSlider/InfiniteSlider';
import MouseSlider from 'components/MouseSlider/MouseSlider';
import RichText, { RichTextProps } from 'components/RichText/RichText';
import Text, { TextProps } from 'components/Text/Text';
import { setTypography, setVh } from 'utils/styles/mixins';
import {
  mediaDesktop,
  mediaDesktopLarge,
  mediaTablet,
} from 'utils/styles/responsive';
import theme, { colors, layout } from 'utils/styles/theme';

const { min, max, fourThirds } = layout.columns;

export interface AboutPageSectionWrapperProps {
  lockWidth?: boolean;
}

interface ExpertiseCategoryButtonProps {
  labelColor?: string;
}

interface ExpertiseCategorySublistHeadingProps {
  categoryTitleColor?: string;
}

export const AboutPageSectionWrapper = styled(
  motion.div
)<AboutPageSectionWrapperProps>`
  display: grid;

  max-width: 100vw;
  overflow: hidden;

  ${({ lockWidth }) =>
    !lockWidth &&
    `
     ${mediaTablet(`
    overflow: unset;
  `)}
  `}

  grid-template-columns: [out-left] 16px [main-section] auto [out-right] 16px;

  ${mediaDesktop(`
    grid-template-columns: [out-left] 124px [main-section] auto [out-right] 124px;
  `)}
`;

export const AboutPageHeadline = styled(DoubleColorHeadline)`
  display: grid;
  place-content: start;
  grid-column: main-section;

  margin: 153px 0 37px;

  // In the future may be used again
  /* min-height: ${setVh(100)}; */
  /* padding: calc(100vh - 475px) 0 154px;

  ${mediaTablet(`
    padding: calc(100vh - 467px) 0 154px;
  `)}

  ${mediaDesktop(`
    padding: calc(100vh - 466px) 0 154px;
  `)}

  ${mediaDesktopLarge(`
    padding: calc(100vh - 362px) 0 154px;
  `)} */
`;

export interface AboutSectionGridProps {
  fullWidth?: boolean;
}
export const AboutSectionGrid = styled(motion.section)<AboutSectionGridProps>`
  position: relative;

  display: grid;
  grid-template-rows: 1fr;

  margin: 80px 0;

  ${mediaTablet(`
    grid-gap: 0 32px;
  `)}

  ${({ fullWidth }) =>
    fullWidth
      ? `
          grid-column: out-left/-1;
        `
      : `
          grid-column: main-section/out-right;
        `}

  grid-template-columns: repeat(
          ${max},
          [content] 1fr
  );
`;

export const ClientsSectionWrapper = styled(AboutSectionGrid)`
  margin: 120px 0 0;

  ${mediaTablet(`
    margin: 255px 0 0;
  `)}
`;

export const AwardsSectionWrapper = styled(AboutSectionGrid)`
  margin: 120px 0 0;

  ${mediaTablet(`
    margin: 240px 0 0;
  `)}
`;

export const LeadershipSectionWrapper = styled(AboutSectionGrid)`
  margin: 0;

  ${mediaTablet(`
    margin: 0;
  `)}
`;

export const SocialMediaSectionWrapper = styled(AboutSectionGrid)`
  margin: 17px 0 93px;

  ${mediaTablet(`
    margin: 35px 0 186px;
  `)}
`;

export const InfoBoxesWrapper = styled.div`
  margin: 40px 0 64px;

  display: flex;
  flex-direction: column;
  gap: 64px;

  grid-column: 1/-1;

  ${mediaTablet(
    `
      margin: 154px 0 106px;
      grid-column: 6/11;
      gap: 114px;
    `
  )}
`;

export const ExpertiseSectionWrapper = styled(AboutSectionGrid)`
  margin: 0;

  ${mediaTablet(
    `
    margin: 184px 0  0;
    `
  )}
`;

export const InfoBoxWrapper = styled(motion.div)``;
export const InfoBoxButtonWrapper = styled(motion.div)`
  grid-column: 1/-1;
  margin-bottom: 40px;
  ${mediaTablet(
    `
      grid-column: 6/11;
      margin-bottom: 153px;
    `
  )}
`;

export const InfoBoxSubTitle = styled(Text)`
  color: ${colors.dustyGray};
`;

export const AboutInfiniteSlider = styled(InfiniteSlider)`
  grid-column: 1/-1;
  width: 100vw;

  margin: 0 0 120px;

  ${mediaTablet(`
    margin: 240px 0 160px
  `)}
`;

export const AnimatedHeaderText = styled(Headline)`
  white-space: nowrap;
  padding-left: 2em;
`;

export const CardWrapper = styled.article`
  width: 70vw;

  ${mediaTablet(`
    width: 23.89vw;
  `)}
`;

export const SocialMediaCardWrapper = styled.article`
  width: 100%;
`;

export const SocialMediaContentWrapper = styled.div`
  grid-column: 1/-1;

  padding-bottom: 56px;
`;

export const SocialMediasWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 32px;

  ${mediaTablet(`
    grid-template-columns: repeat(${min}, 1fr);
  `)}

  ${mediaDesktop(`
    grid-template-columns: repeat(${fourThirds}, 1fr);
  `)}
`;

export const Slider = styled(Draggable)<{ overflowHidden?: boolean }>`
  overflow: ${({ overflowHidden }) => (overflowHidden ? 'hidden' : 'visible')};
  grid-column: 1/-1;

  padding-bottom: 56px;
  cursor: grab;
`;

export const LeadershipSliderContent = styled.div`
  display: flex;
  gap: 32px;
`;

export const LeadershipInfo = styled.div`
  margin: 0 0 35px;
  grid-column: 1/-1;

  display: flex;
  flex-direction: column;
  gap: 2em;
`;

export interface ClientsSectionListProps {
  length?: number;
}
export const ClientsSectionList = styled.ul<ClientsSectionListProps>`
  grid-column: 1/-1;

  display: inline-grid;
  grid-template-columns: repeat(${({ length }) => length}, 250px);
  grid-column-gap: 48px;

  ${mediaDesktopLarge(`
    grid-column: 4/-1;
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    grid-column-gap: 16px;
  `)}
`;

export const ClientSectionColumn = styled.ul`
  grid-column: 1/-1;

  display: grid;
  grid-auto-flow: column;

  ${mediaDesktopLarge(`
    grid-column: span 1;
    display: flex;
    flex-direction: column;
  `)}
`;

export const ClientsSectionSublist = styled.ul`
  margin-bottom: 4em;

  &:hover {
    > li {
      opacity: 0.2;
    }
  }

  > li {
    &:hover {
      opacity: initial;
    }
    transition: opacity 0.4s ease-in-out;
  }
`;

export const ClientsSublistHeader = styled.h3`
  ${({ theme: { locale } }) => setTypography('heading5', locale)}
  margin-bottom: 24px;
  letter-spacing: 0.3em;

  ${mediaDesktopLarge(`
    ${({ theme: { locale } }) => setTypography('heading3', locale)}
  `)}
`;

export interface ClientsSublistItemProps extends TextProps {
  active: boolean;
}
export const ClientsSublistItem = styled(Text)<ClientsSublistItemProps>`
  opacity: ${({ active }) => (active ? 1 : 0.2)};
  transition: opacity 0.4s ease-in-out;

  position: relative;
`;

export const ClientsSectionFilters = styled.ul`
  grid-column: 1/3;

  display: none;

  ${mediaDesktopLarge(`
    display: block;

    height: fit-content;
    position: sticky;
    top: 10%;
  `)}
`;

export interface ClientsSectionFilterProps {
  active?: boolean;
  backgroundColor?: string;
}
export const ClientsSectionFilter = styled.button<ClientsSectionFilterProps>`
  position: relative;

  &::after {
    position: absolute;
    content: '';
    background: ${({ backgroundColor }) =>
      backgroundColor
        ? `${colors[backgroundColor] ?? backgroundColor}`
        : '#fff'};

    height: 1px;
    width: 100%;

    inset: auto auto 0 0;

    transition: transform 0.4s linear;
    transform: scaleX(${({ active }) => (active ? 1 : 0)});
    transform-origin: 0;
  }

  &:hover {
    &::after {
      transform: scaleX(1);
    }
  }
`;

export const ClientsSectionFilterInner = styled(Text)<TextProps>`
  white-space: nowrap;
`;

export const ClientsBackgroundImage = styled.img`
  width: 200px;
  transform: translate(30%, -50%);
`;

export const MobileClientsListWrapper = styled(Draggable)`
  grid-column: 1/-1;

  padding-top: 40px;
`;

export const AboutSectionHeadline = styled.div`
  grid-column: 1/-1;
  margin-bottom: 2em;

  h2 {
    margin-bottom: 18px;
  }

  ${mediaDesktop(`
    grid-column: 1/7;
    margin-bottom: 4em;
  `)}
`;

export const ClientsSectionHeadline = styled(Headline)`
  grid-column: 1/-1;
  margin-bottom: 48px;

  ${mediaDesktop(`
    grid-column: 1/7;
    margin-bottom: 136px;
  `)}
`;

export const Link = styled.a`
  cursor: pointer;
`;

export const ClientLi = styled.li`
  width: fit-content;
`;

export const ClientsListLi = styled.li`
  grid-column: span 1;
  ${mediaDesktop(`

  `)}
`;

export const AwardIcon = styled.img`
  height: 70px;
  margin-right: auto;
`;

export const AwardMiddleContent = styled.div`
  padding: 15px 0 27px;

  margin: 67px 0 20px;

  border-top: 1px solid ${colors.brightGray};
  border-bottom: 1px solid ${colors.brightGray};
`;

export const AwardsGridListItem = styled.li`
  grid-column: span 2;

  display: flex;
  flex-direction: column;
`;

export const Awards = styled(RichText)`
  ${({ theme: { locale } }) => setTypography('smallBody', locale)};
`;

export const AwardsButtonWrapper = styled.div`
  ${({ theme: { locale } }) => setTypography('smallBody', locale)};

  margin-top: 7px;
`;

export const ExpertiseCategoryList = styled.ul`
  grid-column: 1/-1;
  display: flex;
  flex-direction: column;
  row-gap: 48px;

  ${mediaDesktopLarge(`
    display: grid;
    grid-template-columns: repeat(${fourThirds}, [content] 1fr);
    grid-column-gap: 64px;

    row-gap: 0;
  `)}
`;

export const ExpertiseCategorySublist = styled.ul`
  grid-column: span;

  display: flex;
  flex-direction: column;
`;

export const ExpertiseCategorySublistHeading = styled.h3<ExpertiseCategorySublistHeadingProps>`
  margin: 20px 0;
  ${({ theme: { locale } }) => setTypography('heading5', locale)};
  ${({ categoryTitleColor }) =>
    categoryTitleColor &&
    `color: ${colors[categoryTitleColor] ?? categoryTitleColor}`};
`;

export const ExpertiseCategorySublistItem = styled.li`
  display: inline-flex;
  position: relative;

  padding-left: 32px;

  &::after {
    content: '●';
    position: absolute;
    left: 0;
    ${({ theme: { locale } }) => setTypography('smallBody', locale)};
  }
`;

export const ExpertiseCategoryButton = styled.button<ExpertiseCategoryButtonProps>`
  text-align: left;

  span {
    text-decoration: none;

    background-repeat: no-repeat;
    background-position: bottom left;
    background-size: 0 2px;
    transition: background-size 500ms ease-in-out;

    ${({ labelColor }) => css`
      background-image: linear-gradient(
        ${(colors[labelColor] ?? labelColor) || colors.white},
        ${(colors[labelColor] ?? labelColor) || colors.white}
      );
      ${labelColor && `color: ${colors[labelColor] ?? labelColor};`}
    `};

    &:hover {
      background-size: 100% 2px;
    }
  }
`;

export const SliderInner = styled.div`
  display: inline-flex;
  padding: 0 10px;
  height: 56px;
  align-items: center;
  gap: 24px;

  ${mediaTablet(`
    gap: 40px;
  `)}
`;

export const SliderItem = styled.div<{ hoverTextColor?: string }>`
  display: inline-flex;
  cursor: pointer;
  white-space: nowrap;

  ${({ hoverTextColor }) =>
    hoverTextColor &&
    `
    p {
      transition: color 0.2s linear;
      &:hover {
        color: ${colors[hoverTextColor] ?? hoverTextColor}
      }
    }
  `}
`;

export const SliderDivider = styled.span`
  height: 24px;
  width: 1px;
  background: ${colors.white};
  margin-left: 32px;
`;

export const OfficesList = styled(MouseSlider)`
  grid-column: 1/-1;
  overflow: visible;

  margin: 62px 0 56px;
`;

export const OfficesSectionHeadline = styled(AboutSectionHeadline)`
  ${({ theme: { locale } }) => setTypography('heading2', locale)}

  margin: 0;

  ${mediaTablet(`
    margin: 0;
  `)}
`;

export const OfficeInfoWrapper = styled.div`
  grid-column: 1/-1;
  display: flex;
  flex-direction: column;
  gap: 16px;

  ${mediaDesktop(`
    grid-column: 1/7;
    display: grid;
    grid-template-columns: repeat(${fourThirds}, [content] 1fr);
    grid-column-gap: 32px;
    grid-row-gap: 72px;
  `)}
`;

export const OfficeHeading = styled.div`
  grid-column: 1/-1;
`;

export const OfficeInfoBox = styled.div<{ hoverTextColor?: string }>`
  grid-column: span 2;
  display: flex;
  flex-direction: column;
  gap: 20px;

  ${({ hoverTextColor }) => css`
     {
      a:hover {
        color: ${colors[hoverTextColor] ?? hoverTextColor};
      }
    }
  `}
`;

export const OfficeAddresses = styled.div`
  grid-column: 3/6;
`;

export const OfficeInfo = styled.div`
  grid-column: 1/3;
`;

export const TextRich = styled(RichText)<RichTextProps>`
  ${({ theme: { locale } }) => setTypography('smallBody', locale)};
`;

export const OfficeImage = styled.img`
  width: 100%;
  grid-column: 1/-1;

  ${mediaDesktop(`
    grid-column: 6/12;
  `)}
`;

export const HeroSection = styled(Hero)`
  grid-column: 1/-1;

  width: 150%;
  margin-left: -25%;

  overflow-x: hidden;

  ${mediaTablet(`
     width: 100%;
     margin-left: 0;
  `)}
`;

export const CapabilitiesSection = styled(AboutSectionGrid)`
  border-top: 1px solid ${colors.white};

  padding-top: 40px;
  margin: 0 0 120px;

  ${mediaTablet(`
    padding-top: 32px;
    margin: 0 0 96px;
  `)}
`;

export const CapabilitiesSectionDrag = styled(Draggable)`
  grid-column: 1/-1;
`;

export const CapabilitiesSectionDragButton = styled(BaseButton)`
  position: absolute;
  inset: auto auto 12px 12px;
`;

export const CapabilitiesSectionDragTitle = styled(Text)`
  position: absolute;
  inset: 12px auto auto 12px;
`;

export interface CapabilitiesSectionDragInnerProps {
  items: number;
}
export const CapabilitiesSectionDragInner = styled.div<CapabilitiesSectionDragInnerProps>`
  display: inline-grid;
  grid-template-columns: ${({ items }) => `repeat(${items}, 80vw)`};
  grid-gap: 16px;
  padding: 64px 0;
`;

export const CapabilitiesSectionMobileSlide = styled.div`
  display: block;
  position: relative;
`;

export const CapabilitiesCarousel = styled(Carousel)`
  width: 100%;
  height: min-content !important;

  aspect-ratio: 16/9;

  margin: 64px 0 !important;
  overflow: hidden;

  grid-column: 1/-1;

  ${mediaDesktop(`
    grid-column: 6/-1;
    margin: 64px 0 !important;
  `)}
`;

export const HeadingWrapper = styled.div`
  grid-column: 1/-1;

  top: 4%;

  z-index: ${theme.layout.zIndex.overContent};

  ${mediaDesktop(`
    position: sticky;
    grid-column: 1/6;
    top: 14%;
  `)}
`;

export const CapabilitiesHeadline = styled.div`
  padding: 16px 50px 0 0;
`;

export const CapabilitiesDescription = styled.article`
  margin-top: 50px;

  grid-column: 1/-1;

  ${mediaDesktop(`
    grid-column: 6/-1;
    margin-top: 200px;
  `)}
`;

export const AboutFiltersHeading = styled.h5`
  ${({ theme: { locale } }) => setTypography('heading5', locale)}

  margin-bottom: 20px;

  grid-column: 1/-1;
  ${mediaDesktop(`
    grid-column: 6/-1;
    margin-bottom: 35px;
  `)}
`;

export const AboutFilters = styled.div`
  display: grid;

  grid-template-columns: 1fr;
  grid-column: 1/-1;

  ${mediaDesktop(`
    grid-template-columns: 1fr 1fr;

    grid-column: 6/-1;
  `)}
`;

export const Filter = styled(BaseFilter)``;

export const OfficeSectionGrid = styled(AboutSectionGrid)`
  margin-top: 60px;

  ${mediaDesktop(`
    margin-top: 241px;
  `)}
`;

export const TextContentSectionGrid = styled(AboutSectionGrid)`
  margin-top: 0;
  margin-bottom: 70px;

  ${mediaDesktop(`
    margin-bottom: 105px;
  `)}
`;

export const TextContentSectionWrapper = styled.div`
  grid-column: 1/-1;

  & > div:first-child {
    padding-bottom: 32px;
  }

  div {
    h1,
    h1 span {
      ${({ theme: { locale } }) => setTypography('heading1', locale)};
    }
    h2,
    h2 span {
      ${({ theme: { locale } }) => setTypography('heading2', locale)};
    }
    h3,
    h3 span {
      ${({ theme: { locale } }) => setTypography('heading3', locale)};
    }
    h4,
    h4 span {
      ${({ theme: { locale } }) => setTypography('heading4', locale)};
    }
    h5,
    h5 span {
      ${({ theme: { locale } }) => setTypography('heading5', locale)};
    }
    h6,
    h6 span {
      ${({ theme: { locale } }) => setTypography('body', locale)};
    }
  }
`;

export const DoubleColumnTextSectionGrid = styled(AboutSectionGrid)`
  margin-top: 0;
  margin-bottom: 92px;

  ${mediaTablet(`
    margin-bottom: 183px;
  `)}
`;

export const DoubleColumnTextSectionWrapper = styled(TextContentSectionWrapper)`
  border-top: 1px solid ${colors.brightGray};
  padding-top: 51px;

  ${mediaTablet(`
    padding-top: 102px;
  `)}
`;

export const DoubleColumnTextSectionColumns = styled.div`
  display: grid;
  grid-template-columns: 1fr;

  & > div:first-child {
    padding-bottom: 24px;
  }

  ${mediaTablet(`
    grid-column-gap: 105px;
    grid-template-columns: repeat(${max}, 1fr);

    & > div {
      grid-column: 6/12;
    }

    & > div:first-child {
      grid-column: 1/6;
      padding-bottom: 0;
    }
  `)}
`;
